import lucene from 'lucene'
import moment from 'moment-timezone'
import { Query } from '@skedulo/protobuf-web'
import {
  LuceneNode,
  ComparatorType,
  ParsedNode,
  LuceneNodeWithImplicitChildren,
} from '../types'
import {
  defaultNodeRangedTermFields,
  defaultNodeTermFields,
} from './lucene-utils'

// Name = equals
// -Name = not equals
const FIELD_RE = /^(-)?(.*)$/

// *something* = contains
// something* = startsWith
// something = exact
const TERM_RE = /^(\*)?(.*?)(\*)?$/

const parseNodeWithImplicitChildrenValues = (
  node: lucene.AST | lucene.Node,
): string[] => {
  if ('term' in node) {
    return [node.term]
  }
  if ('right' in node) {
    return [
      ...parseNodeWithImplicitChildrenValues(node.left),
      ...parseNodeWithImplicitChildrenValues(node.right),
    ]
  }
  return []
}

// The lucene parser handles most escaping, but some characters aren't handled
const specialCharacters = [
  '\\',
  ' ',
  '+',
  '-',
  '!',
  '(',
  ')',
  ':',
  '^',
  '[',
  ']',
  '"',
  '{',
  '}',
  '~',
  '*',
  '?',
  '|',
  '&',
  '/',
]
export const escapeTerm = (term: string) =>
  specialCharacters.reduce(
    (result: string, current: string) =>
      result.replaceAll(current, `\\${current}`),
    term,
  )
const unescapeTerm = (term: string) =>
  specialCharacters.reduce(
    (result: string, current: string) =>
      result.replaceAll(`\\${current}`, current),
    term,
  )

// The query becomes invalid if we don't put something in place of an empty value
// in a range query. Ideally `*` would be used but then it's impossible to tell
// if a query is greater or less than from the text alone.
const EMPTY_RANGE_PLACEHOLDER = '?'
const escapeEmptyRange = (s: string) => (!s ? EMPTY_RANGE_PLACEHOLDER : s)
const unescapeEmptyRange = (s: string) =>
  s === EMPTY_RANGE_PLACEHOLDER ? '' : s

const IN_OPERATOR_REGEX = /\$in(\\\[.*\\])/

export const parseNode = (node: LuceneNode): ParsedNode => {
  let fieldName = node.field
  let negated = false
  let termValue: string | string[] = ''
  let comparator: ComparatorType = 'EQUALS'

  const parsedField = FIELD_RE.exec(node.field)
  if (parsedField) {
    fieldName = parsedField[2]
    negated = !!parsedField[1]
    if (parsedField[1]) {
      negated = true
      comparator = 'NOT_EQUALS'
    }
  }

  if ('term' in node) {
    termValue = unescapeTerm(node.term)

    const parsedTerm = TERM_RE.exec(termValue)
    if (parsedTerm) {
      termValue = parsedTerm[2]
      if (parsedTerm[1] && parsedTerm[3]) {
        if (negated) {
          comparator = 'NOT_CONTAINS'
        } else {
          comparator = 'CONTAINS'
        }
      } else if (parsedTerm[1] || parsedTerm[3]) {
        if (negated) {
          comparator = 'NOT_STARTS_WITH'
        } else {
          comparator = 'STARTS_WITH'
        }
      }
    }

    const [, inOperatorValues] = node.term.match(IN_OPERATOR_REGEX) || [
      undefined,
      undefined,
    ]
    if (inOperatorValues) {
      try {
        termValue = JSON.parse(unescapeTerm(inOperatorValues))
      } catch (error) {
        // Shouldn't be possible to get here but if it happens log and silently reset values
        console.error(
          'Error parsing IN operator values',
          unescapeTerm(inOperatorValues),
        )
        termValue = []
      }
      comparator = negated ? 'NOT_IN' : 'IN'
    }
  }

  if ('inclusive' in node) {
    if (node.term_min === '*') {
      termValue = unescapeEmptyRange(node.term_max)
      comparator =
        node.inclusive === 'none' || node.inclusive === 'left'
          ? 'LESS'
          : 'LESS_EQUAL'
    } else if (node.term_max === '*') {
      termValue = unescapeEmptyRange(node.term_min)
      comparator =
        node.inclusive === 'none' || node.inclusive === 'right'
          ? 'GREATER'
          : 'GREATER_EQUAL'
    } else {
      termValue = [
        unescapeEmptyRange(node.term_min),
        unescapeEmptyRange(node.term_max),
      ]
      comparator = 'BETWEEN' // for now we only support inclusive (this is validated by canHandleNode in lucene.ts)
    }
  }

  if ('left' in node) {
    termValue = parseNodeWithImplicitChildrenValues(node)
    if (node.operator === 'OR') {
      comparator = negated ? 'NONE_OF' : 'ONE_OF'
    } else {
      comparator = negated ? 'NOT_EQUALS' : 'EQUALS'
    }
  }

  return { fieldName, term: termValue, comparator }
}

const getRangeNodeFields = (
  termValue: string | string[],
  comparator: ComparatorType,
): {
  inclusive: 'both' | 'right' | 'left' | 'none'
  term_min: string
  term_max: string
} => {
  if (Array.isArray(termValue)) {
    if (comparator === 'BETWEEN') {
      if (termValue.length === 2) {
        return {
          inclusive: 'both',
          term_min: escapeEmptyRange(termValue[0]),
          term_max: escapeEmptyRange(termValue[1]),
        }
      }
      if (termValue.length === 1) {
        return {
          inclusive: 'both',
          term_min: escapeEmptyRange(termValue[0]),
          term_max: escapeEmptyRange(termValue[0]),
        }
      }
    }
  } else {
    switch (comparator) {
      case 'GREATER':
        return {
          inclusive: 'right',
          term_min: escapeEmptyRange(termValue),
          term_max: '*',
        }
      case 'GREATER_EQUAL':
        return {
          inclusive: 'both',
          term_min: escapeEmptyRange(termValue),
          term_max: '*',
        }
      case 'LESS':
        return {
          inclusive: 'left',
          term_min: '*',
          term_max: escapeEmptyRange(termValue),
        }
      case 'LESS_EQUAL':
        return {
          inclusive: 'both',
          term_min: '*',
          term_max: escapeEmptyRange(termValue),
        }
    }
  }

  throw Error('Invalid comparator type')
}

const getChildNode = (
  field: string,
  terms: string[],
  operator: 'OR' | 'AND',
  parenthesized = false,
): lucene.Node | LuceneNodeWithImplicitChildren => {
  if (terms.length <= 1) {
    return getNode(field, terms[0] ?? '', 'EQUALS')
  }

  return {
    field,
    left: getNode('<implicit>', terms[0], 'EQUALS'),
    operator,
    right: getChildNode('<implicit>', terms.slice(1), operator),
    parenthesized,
  }
}

const NEGATED_TYPES = new Set<ComparatorType>([
  'NOT_EQUALS',
  'NOT_CONTAINS',
  'NOT_STARTS_WITH',
  'NONE_OF',
  'NOT_IN',
])
const RANGE_TYPES = new Set<ComparatorType>([
  'GREATER',
  'GREATER_EQUAL',
  'LESS',
  'LESS_EQUAL',
  'BETWEEN',
])

/**
 * This is used to build nodes in the filter dropdowns to match the Lucene node format.
 * At this point, it is just a field with a value and comparator type.  It has no knowledge of the Lucene query syntax.
 */
export const getNode = (
  fieldName: string,
  termValue: string | string[],
  comparator: ComparatorType,
): lucene.Node | LuceneNodeWithImplicitChildren => {
  // Not in NEGATED_TYPES/RANGE_TYPES, so both would otherwise fall through to the byte-identical
  // `Field:""` node below - fail loudly instead of silently matching on an empty string.
  if (comparator === 'IS_NULL' || comparator === 'IS_NOT_NULL') {
    throw new Error(
      `Comparator ${comparator} is not supported by the Lucene builder`,
    )
  }

  const field = `${NEGATED_TYPES.has(comparator) ? '-' : ''}${fieldName}`

  if (RANGE_TYPES.has(comparator)) {
    return {
      ...defaultNodeRangedTermFields,
      field,
      ...getRangeNodeFields(termValue, comparator),
    }
  } else if (Array.isArray(termValue)) {
    if (comparator === 'IN' || comparator === 'NOT_IN') {
      const term = `$in${JSON.stringify(termValue)}`
      const escapedTerm = escapeTerm(term)
      return {
        ...defaultNodeTermFields,
        field,
        term: escapedTerm,
        quoted: false,
      }
    }
    return getChildNode(
      field,
      termValue,
      comparator === 'ONE_OF' || comparator === 'NONE_OF' ? 'OR' : 'AND',
      true,
    )
  } else {
    let term = escapeTerm(termValue)
    switch (comparator) {
      case 'CONTAINS':
      case 'NOT_CONTAINS':
        term = `*${term}*`
        break
      case 'STARTS_WITH':
      case 'NOT_STARTS_WITH':
        term = `${term}*`
        break
    }
    return {
      ...defaultNodeTermFields,
      field,
      term,
      // Only quote the term when it is falsy to avoid any errors.
      // No need to quote in other cases as special characters should be escaped.
      quoted: !term,
    }
  }
}

export const serializeDate = (d: Date) => moment(d).format('YYYY-MM-DD')

// This EXACT format is required for the GraphQL backend: 2021-03-03T00:10:00.123Z
export const serializeDateTime = (d: Date) =>
  moment(d).utc().format('YYYY-MM-DD[T]HH:mm:ss.SSS[Z]')

/**
 *
 * Returns the default value for each filter comparator type,
 * For example, IN, NOT_IN, BETWEEN, etc. This is used as the default value for each filter dropdown type.
 * It is mostly used to handle arrays and dates, otherwise it defaults to an empty string.
 */
export const getDefaultValueByComparator = (
  comparator: ComparatorType,
  fieldType?: Query.API.DataField_Kind,
): string[] | string => {
  if (comparator === 'IS_NULL' || comparator === 'IS_NOT_NULL') return ''

  // Not sure why dates default to today if user has not entered anything.  That doesn't look right.
  if (fieldType === Query.API.DataField_Kind.DATE) {
    if (comparator === 'BETWEEN') return getSerializedDateRange(new Date())
    else return serializeDate(new Date())
  }

  if (fieldType === Query.API.DataField_Kind.DATETIME) {
    if (comparator === 'BETWEEN') return getSerializedDateTimeRange(new Date())
    else return serializeDateTime(new Date())
  }

  if (comparator === 'IN' || comparator === 'NOT_IN') return []

  if (comparator === 'BETWEEN') return ['', '']
  return ''
}

/**
 * Date utils - these functions are duplicated in Horizon Foundation
 */

const getSerializedDateRange = (date: Date) => [
  serializeDate(date),
  serializeDate(date),
]

const getStartOfDay = (date: Date) => {
  const res = new Date(date)
  res.setHours(0, 0, 0, 0)
  return res
}
const getEndOfDay = (date: Date) => {
  const res = new Date(date)
  res.setHours(23, 59, 59, 999)
  return res
}

const getSerializedDateTimeRange = (date: Date) => [
  serializeDateTime(getStartOfDay(date)),
  serializeDateTime(getEndOfDay(date)),
]
